import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ChartLineUp,
  Detective,
  PenNib,
  Image as ImageIcon,
  MagnifyingGlass,
  CalendarBlank,
  Broadcast,
  UserCircle,
  SignOut,
  Waveform,
  CreditCard,
  ShieldStar,
  Bell,
} from "@phosphor-icons/react";

const navGroups = [
  { section: "REPUTATION", items: [
    { to: "/", label: "Overview", icon: ChartLineUp, end: true, id: "overview" },
    { to: "/reputation", label: "Reputation Scan", icon: Detective, id: "reputation" },
    { to: "/monitoring", label: "Monitoring & Alerts", icon: Bell, id: "monitoring" },
  ]},
  { section: "GROWTH", items: [
    { to: "/content", label: "Content Studio", icon: PenNib, id: "content" },
    { to: "/images", label: "Image Studio", icon: ImageIcon, id: "images" },
    { to: "/seo", label: "SEO Engine", icon: MagnifyingGlass, id: "seo" },
    { to: "/social", label: "Auto-Posting", icon: Broadcast, id: "social" },
    { to: "/calendar", label: "Content Calendar", icon: CalendarBlank, id: "calendar" },
  ]},
  { section: "BRAND", items: [
    { to: "/profile", label: "Brand Profile", icon: UserCircle, id: "profile" },
    { to: "/billing", label: "Billing & Plans", icon: CreditCard, id: "billing" },
  ]},
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const groups = user?.role === "admin"
    ? [...navGroups, { section: "ADMIN", items: [{ to: "/admin", label: "Admin Console", icon: ShieldStar, id: "admin" }] }]
    : navGroups;
  const items = groups.flatMap((g) => g.items);
  const current = items.find((n) => (n.end ? location.pathname === "/" : location.pathname.startsWith(n.to)));

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-[#070707] flex flex-col fixed h-screen z-20" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-border flex items-center gap-2">
          <Waveform size={26} weight="fill" className="text-primary" />
          <div>
            <div className="font-head font-bold text-2xl leading-none tracking-tighter gold-foil">SMI</div>
            <div className="overline text-muted-foreground mt-1">SOCIAL MEDIA INFLUENCER</div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-4 overflow-y-auto">
          {groups.map((group) => (
            <div key={group.section} className="space-y-1">
              <div className="overline text-muted-foreground/60 px-3 pb-1">{group.section}</div>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={`nav-${item.id}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-[color,background-color,border-color] duration-200 border-l-2 ${
                        isActive
                          ? "bg-primary/10 text-primary border-primary"
                          : "text-muted-foreground border-transparent hover:text-white hover:bg-secondary/20"
                      }`
                    }
                  >
                    <Icon size={18} weight="bold" />
                    {item.label}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 bg-primary/15 border border-primary/30 flex items-center justify-center font-mono text-sm text-primary uppercase">
              {(user?.name || "U").slice(0, 2)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-destructive hover:bg-secondary transition-colors duration-200"
          >
            <SignOut size={18} weight="bold" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 min-h-screen">
        <header className="h-16 border-b border-border flex items-center px-8 sticky top-0 glass z-10">
          <h1 className="font-head font-bold text-2xl tracking-tight" data-testid="page-title">
            {current?.label || "Overview"}
          </h1>
          <div className="ml-auto flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-primary animate-pulse shadow-[0_0_8px_rgba(233,172,12,0.8)]" />
            <span className="overline text-primary">ENGINE ACTIVE · 24/7</span>
          </div>
        </header>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
